import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import PortalLayout from '../../components/PortalLayout';
import { getPublicRating, submitPublicRating, errorMessage } from '../../services/customerApi';
import { useLanguage } from '../../i18n/LanguageContext';

/**
 * Pagina pubblica per valutare l'assistenza (da 1 a 5 stelle + commento). Si apre dal link nell'email di chiusura,
 * con il voto già scelto (?score=): il cliente lo conferma con "Invia", così un clic involontario non vota.
 */
function PortalRate() {
  const { t } = useLanguage();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [score, setScore] = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    getPublicRating(token)
      .then((data) => {
        setInfo(data);
        const fromLink = Number(searchParams.get('score'));
        setScore(data.rating ? data.rating.rating : (fromLink >= 1 && fromLink <= 5 ? fromLink : 0));
        setComment(data.rating?.comment || '');
      })
      .catch((err) => setError(errorMessage(err, t('portal.trackNotFound'))))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (score < 1) return;
    setSaving(true);
    setError('');
    try {
      await submitPublicRating(token, score, comment.trim());
      setDone(true);
    } catch (err) {
      setError(errorMessage(err, t('portal.rateError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PortalLayout>
      <div className="card portal-rate">
        <div className="card-body">
          {loading && <div className="loading">{t('common.loading')}</div>}
          {error && <div className="error-message">{error}</div>}
          {info && !info.can_rate && !done && (
            <p>{info.enabled ? t('portal.rateNotYet') : t('portal.rateDisabled')}</p>
          )}
          {info && info.can_rate && !done && (
            <form onSubmit={submit}>
              <h2 className="portal-ticket-title">{t('portal.rateTitle')}</h2>
              <p className="portal-rate-subject">#{info.ticket_id} · {info.subject}</p>
              <div className="portal-stars" role="radiogroup" aria-label={t('portal.rateTitle')}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={score === n}
                    className={'portal-star' + (n <= score ? ' portal-star-on' : '')}
                    onClick={() => setScore(n)}
                    aria-label={t('portal.rateStars', { count: n })}
                  >
                    ★
                  </button>
                ))}
              </div>
              <p className="portal-rate-scale">{score > 0 ? t(`portal.rateScale_${score}`) : t('portal.ratePick')}</p>
              <div className="form-group">
                <label>{t('portal.rateComment')}</label>
                <textarea rows={4} value={comment} maxLength={1000} onChange={(e) => setComment(e.target.value)} />
              </div>
              <button type="submit" className="btn" disabled={saving || score < 1}>{saving ? t('common.saving') : t('portal.rateSend')}</button>
              {info.rating && <p className="tp-hint">{t('portal.rateAlready')}</p>}
            </form>
          )}
          {done && (
            <div className="portal-rate-done">
              <h2 className="portal-ticket-title">{t('portal.rateThanks')}</h2>
              <p>{t('portal.rateThanksBody')}</p>
            </div>
          )}
          {info && <p className="portal-rate-back"><Link to={`/portal/ticket/${token}`}>{t('portal.rateBack')}</Link></p>}
        </div>
      </div>
    </PortalLayout>
  );
}

export default PortalRate;
